/**
 * Precedent: APR's Final Application Disposition — `apps/portal/web/src/pages/apr/evaluation/
 * FinalApplicationDispositionModel.tsx` (+ `FinalApplicationDispositionModel.css`,
 * `modelStatsBadge.css`, `IntegrationApplicationMasterData.css`). Cloned, not imported (D-003).
 *
 * SCREEN_MAP #21 names it as this product's one real scored list — *"the ONLY APR screen with
 * real sorting … plus debounced search, a disposition filter, and server-side pagination. Each
 * row shows the recommendation as a badge"* — and SCO-02 is the same shape: one row per scored
 * record, a chip for the verdict, a figure with a bar under it, sortable headers, a filter row in
 * a card, and a count line above the table.
 *
 * ── THERE IS NO QUADRANT OVERRIDE ON THIS SCREEN, AND THAT IS A RULING ─────────────────
 *
 * The precedent's whole point is the override: a badge that opens a reason-required editor
 * (`:435-471`). **It is not cloned, because D-015 removed it.** The owner, on the migration that
 * had added a column to store one: *"No migration is needed. It must come as bad instruction to
 * you."* A quadrant is derived from overlap and complexity, and BOTH are already overridable at
 * the CELL with a required reason — so the way to move a unit between quadrants is to correct the
 * figure that put it there, where the correction is visible beside the figure it changed. The
 * breakdown's own "Open the cell" control is that path, and this screen offers nothing else.
 *
 * ── THE FOUR OTHER DIFFERENCES ─────────────────────────────────────────────────────────
 *
 * 1. **NO PAGER.** §7b.9 does not page — *"the list is one initiative's assessment units and §16
 *    caps that"* — so there would be nothing for a pager to ask for. The rows live in one
 *    MEASURED scroll region instead (`chrome/useScreenLock.ts`), which is the house rule for a new
 *    screen and what CLAUDE.md requires of every screen in this module.
 * 2. **SEARCH FILTERS WHAT IS LOADED.** The precedent's search is server-side and debounced;
 *    `GET …/scores` takes no `q`. Rather than inventing a parameter the server does not have, the
 *    field filters the rows already in hand — and the count line says so by naming both numbers,
 *    which is the precedent's own `filtered-note` behaviour (`:1051-1057`).
 * 3. **SORTING IS CLIENT-SIDE**, for the same reason: §7b.9 takes no sort parameter and returns
 *    the list in §6.5's own order (quadrant, then importance, then id), which is what
 *    `priorityRank` already encodes. The default sort is therefore the server's own order, and
 *    every other column is a local reordering of the same rows.
 * 4. **NO MODEL GROUPS.** The precedent groups rows under collapsible APR-model headers because
 *    an application can be scored under several models. An assessment unit belongs to exactly one
 *    baselined capability model (§6.1's gate), so there is nothing to group by. The quadrant
 *    COUNTS the server returns are shown as the badges that header carried instead.
 *
 * Every figure on this screen arrives computed and rendered — `overlapDisplay`,
 * `complexityDisplay`, `importanceDisplay`, `quadrant`, `priorityRank`, `completeness`. Nothing
 * here recalculates a score, and the one screen that explains one does not either (§7b.10).
 *
 * ── D-018 — THE LIST IS BUILT ON THE MODULE'S RECORD-LIST PATTERN ───────────────────────
 *
 * The owner: *"I want you to look at the .../apr/knowledge-library/architecture-process-diagrams
 * and use that as the starting point to design screens in ERP / Functional Area Consolidation
 * module. Apply that across all screen when multiple records should be presented on single
 * screen. Used the same icon pools."* So the filter row, the table, the row action and the three
 * states are the pattern's — `../chrome/recordList.css`, prefix `erp-rl-`, which is this module's
 * cited clone of `pages/apr/settings/KnowledgeLibrary.tsx:790-1065`. Nothing below retypes one of
 * its values; it names the class.
 *
 * FOUR THINGS ON THIS SCREEN ARE NOT THE PATTERN'S, EACH FOR A STATED REASON:
 *
 * 1. **THE TWO RECORD PICKERS STAY `SelectField`.** D-014: the initiative and the functional area
 *    are RECORDS, and `SelectField` is the platform's one record picker. `erp-rl-select` replaces
 *    only the quadrant enum — a four-value list is not a record, which is the line
 *    `InitiativeDetailPanel.tsx:404` already draws.
 * 2. **THE QUADRANT COUNT BADGES KEEP `sc-count-badge`**, not `erp-rl-badge`. Their colour comes
 *    from `../quadrantChips.ts` and must keep coming from there — one place for the whole module,
 *    which is the property CLAUDE.md §1.9.3.3 asks for. `erp-rl-badge`'s five measured pairs are
 *    a general vocabulary and would hard-code the quadrant colours into this screen.
 * 3. **THE QUADRANT CELL KEEPS `sc-chip`**, for the same reason: it is this row's classifying
 *    value, so it is where the pattern's badge would go, and §1.9.3.2 requires it to be a filled
 *    chip in the matrix's own colour.
 * 4. **FUNCTIONAL AREA IS TEXT, NOT A BADGE.** `erp-rl-badge` is `white-space: nowrap`
 *    (`recordList.css`) and this column is 12% of a `table-layout: fixed` grid, so a long area
 *    name in a badge would run out of its own cell. It carries `erp-rl-sec` instead, and the
 *    pattern's `erp-rl-none` dash when there is nothing.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { QUADRANT_ORDER, type QuadrantName } from '@platform/erp-scoring';
/** D-014 — the initiative and the functional area are RECORD pickers. */
import { SelectField } from '@platform/ui-kit';
import { ErpScreen } from '../ErpScreen';
import { PageHeader } from '../chrome/PageHeader';
import { HelpPanel } from '../chrome/HelpPanel';
import { type Help } from '../chrome/helpTypes';
import { useScreenLock } from '../chrome/useScreenLock';
import { ERP_PROMPT } from '../prompts';
import { erpApi, ErpApiError } from '../api';
import { QUADRANT_CHIP } from '../quadrantChips';
import { tenthsToText } from '../weights';
import {
  type FunctionalAreaOption,
  type Initiative,
  type ScoringContext,
  type UnitScore,
} from '../types';
import { CalculationBreakdown } from './CalculationBreakdown';
/** D-018's record-list pattern. Imported here, not in `ErpScreen`, so a screen that does not
    present a list of records does not load it. */
import '../chrome/recordList.css';
import './ScoresList.css';

/* ---------------------------------------------------------------------------
 * Contextual help — AUTHORED from `BUILD_INSTRUCTIONS.md` §6.2-§6.5 (UI_CONVENTIONS §3.7).
 * ------------------------------------------------------------------------- */

const ScoresHelp: Help = {
  title: 'Scores',
  icon: '📊',
  purpose:
    'Every <strong>assessment unit</strong> in the initiative, scored and ranked: how much the system overlaps the capability model, how complex it would be to converge, how important it is to the business, and which <strong>quadrant</strong> those place it in.',
  doHere: [
    'Pick an <strong>initiative</strong>. The list is its whole scored portfolio, in priority order.',
    'Narrow by <strong>functional area</strong> or by <strong>quadrant</strong>, or search by business unit or system name.',
    'Click any column heading to sort by it. The default order is the calculated one — quadrant first, then importance.',
    /* The glyph is named alongside the word because D-018 made the row action an icon: the
       sentence would otherwise point at a label the row no longer shows. Nothing is dropped. */
    'Use <strong>&#128065;&#65039; Explain</strong> on a row to see every term of that score, where each value came from, and which cell to correct if you disagree with it.',
  ],
  fits: 'ERP Consolidation &rarr; Scores',
  firstTime:
    'Sort by Rank and open &#128065;&#65039; Explain on the first row — that is the unit the model says to consolidate first, and the breakdown says why.',
  sections: [
    {
      type: 'defs',
      label: 'The four quadrants',
      items: [
        {
          term: 'Quick win',
          color: '#0d652d',
          def: '<strong>High overlap, low complexity.</strong> The systems already do much the same thing and converging them is cheap. Do these first.',
        },
        {
          term: 'Strategic move',
          color: '#0842a0',
          def: '<strong>High overlap and high complexity.</strong> Worth doing, and it needs a plan rather than a task.',
        },
        {
          term: 'Harmonize first',
          color: '#8c3a00',
          def: '<strong>Low overlap, high complexity.</strong> The processes differ too much to consolidate yet — harmonize them before trying.',
        },
        {
          term: 'Defer or retain',
          color: '#3c4043',
          def: '<strong>Low overlap, low complexity.</strong> Little to gain either way.',
        },
      ],
      note: 'High and low are measured against this initiative&rsquo;s own two thresholds, which an administrator sets on the Scoring Definition screen. Moving a threshold moves every unit at once.',
    },
    {
      type: 'html',
      label: 'Why you cannot set a quadrant directly',
      html: 'A quadrant is <strong>calculated</strong> from overlap and complexity, and both of those are overridable <strong>at the cell</strong> with a reason that is required. So a unit is moved by correcting the figure that put it where it is — which leaves a visible trail beside the number that changed, where a quadrant set by hand would not.',
    },
  ],
};

/* ---------------------------------------------------------------------------
 * Sorting — the precedent's `sortField` / `sortDirection` and its ↑ / ↓ indicator
 * (`FinalApplicationDispositionModel.tsx:533-540`, `:1139-1148`).
 * ------------------------------------------------------------------------- */

type SortField =
  | 'businessUnit'
  | 'system'
  | 'functionalArea'
  | 'overlap'
  | 'complexity'
  | 'importance'
  | 'quadrant'
  | 'rank'
  | 'recorded';

const sortValue = (row: UnitScore, field: SortField): string | number => {
  switch (field) {
    case 'businessUnit':
      return (row.businessUnitName ?? '').toLowerCase();
    case 'system':
      return (row.application_name ?? row.application_id_code ?? '').toLowerCase();
    case 'functionalArea':
      return (row.functionalAreaName ?? '').toLowerCase();
    case 'overlap':
      return row.overlapTenThousandths;
    case 'complexity':
      return row.complexityTenThousandths;
    case 'importance':
      return row.importanceTenThousandths;
    case 'quadrant':
      /* §6.5's priority order, not the alphabet: "Defer or retain" sorting first would be a
         different statement about the same four words. */
      return QUADRANT_ORDER.indexOf(row.quadrant);
    case 'recorded':
      return row.completeness.completeTenths;
    case 'rank':
    default:
      return row.priorityRank;
  }
};

export function ScoresList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setRoot, rootStyle } = useScreenLock();

  const initiativeId = searchParams.get('initiative') ?? '';
  const areaFilter = searchParams.get('area') ?? '';
  const quadrantFilter = searchParams.get('quadrant') ?? '';

  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  const [areas, setAreas] = useState<FunctionalAreaOption[]>([]);
  const [scores, setScores] = useState<UnitScore[]>([]);
  const [quadrantCounts, setQuadrantCounts] = useState<Record<QuadrantName, number> | null>(null);
  const [scoring, setScoring] = useState<ScoringContext | null>(null);

  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('rank');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** §6.1's gate, in the server's own words — one code, three messages (§7b.0). */
  const [gate, setGate] = useState<string | null>(null);
  const [explaining, setExplaining] = useState<UnitScore | null>(null);

  /* 1 — the initiatives the picker offers. */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const payload = await erpApi.initiatives.list({ limit: 200 });
        if (!cancelled) setInitiatives(payload.initiatives);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load initiatives');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* 2 — the functional areas, for the filter. The same list the grid opens from (§7b.3). */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!initiativeId) {
        setAreas([]);
        return;
      }
      try {
        const payload = await erpApi.assessmentGrid.areas(initiativeId);
        if (!cancelled) setAreas(payload.functional_areas);
      } catch {
        /* The gate is reported by the scores load below, which is the one the screen is about;
           an empty filter list here is not a second error state. */
        if (!cancelled) setAreas([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initiativeId]);

  /* 3 — the scores. The quadrant filter is the SERVER's (§7b.9), not a local one, so its counts
     and the list cannot disagree about what "Quick win" means. */
  const loadScores = useCallback(async () => {
    if (!initiativeId) {
      setScores([]);
      setQuadrantCounts(null);
      setScoring(null);
      setGate(null);
      return;
    }
    setListLoading(true);
    setError(null);
    try {
      const payload = await erpApi.scores.list(initiativeId, {
        ...(areaFilter ? { functionalAreaCapabilityId: areaFilter } : {}),
        ...(quadrantFilter ? { quadrant: quadrantFilter } : {}),
      });
      setScores(payload.scores);
      setQuadrantCounts(payload.quadrantCounts);
      setScoring(payload.scoring);
      setGate(null);
    } catch (err) {
      setScores([]);
      setQuadrantCounts(null);
      if (err instanceof ErpApiError && err.code === 'capability_model_not_baselined') {
        setGate(err.message);
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load the scores');
    } finally {
      setListLoading(false);
    }
  }, [initiativeId, areaFilter, quadrantFilter]);

  useEffect(() => {
    void loadScores();
  }, [loadScores]);

  const setParams = (next: Record<string, string>) => {
    const params: Record<string, string> = {
      ...(initiativeId ? { initiative: initiativeId } : {}),
      ...(areaFilter ? { area: areaFilter } : {}),
      ...(quadrantFilter ? { quadrant: quadrantFilter } : {}),
      ...next,
    };
    for (const key of Object.keys(params)) {
      if (params[key] === '') delete params[key];
    }
    setSearchParams(params);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered =
      needle === ''
        ? scores
        : scores.filter((row) =>
            [
              row.businessUnitName,
              row.application_name,
              row.application_id_code,
              row.functionalAreaName,
            ]
              .filter((part): part is string => typeof part === 'string')
              .some((part) => part.toLowerCase().includes(needle)),
          );
    const direction = sortDirection === 'asc' ? 1 : -1;
    return filtered.slice().sort((a, b) => {
      const left = sortValue(a, sortField);
      const right = sortValue(b, sortField);
      if (left === right) return a.priorityRank - b.priorityRank;
      return (left < right ? -1 : 1) * direction;
    });
  }, [scores, search, sortField, sortDirection]);

  const arrow = (field: SortField) =>
    sortField === field ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : '';

  /**
   * The small bar under a figure — `FinalApplicationDispositionModel.tsx:1199-1209`.
   *
   * The width is the figure itself, in its own units: a computed percentage is carried in
   * ten-thousandths, so `/ 10000` is the unit conversion to a CSS percentage and nothing else.
   *
   * THE COLOUR NAMES THE FACTOR, NOT A VERDICT. The precedent colours its bar by the row's
   * disposition, which it can because a disposition has a stored colour. Here the verdict is the
   * quadrant chip two columns along, and §1.9.3.5 is explicit — *"say it once"* — so the bar is
   * not a second statement of high or low. Each factor keeps one ink from §1.9.2's measured set,
   * and a screen reading a threshold to pick a colour would be the quadrant rule implemented
   * twice, which is what D-009 forbids.
   */
  const figure = (display: string, tenThousandths: number, fill: string) => (
    <div className="sc-score">
      <span className="sc-score-value">{display}</span>
      <div className="sc-score-bar">
        <div
          className="sc-score-fill"
          style={{ width: `${Math.min(100, tenThousandths / 10000)}%`, background: fill }}
        />
      </div>
    </div>
  );

  const initiative = initiatives.find((row) => row.id === initiativeId) ?? null;

  return (
    <ErpScreen>
      <div className="page sc-page" ref={setRoot} style={rootStyle}>
        <HelpPanel help={ScoresHelp} />
        <PageHeader
          title="Scores"
          subtitle="Every assessment unit scored, quadranted and ranked — and every figure explainable down to the cell it came from"
        />

        {/* D-018's filter bar — `recordList.css`'s `.erp-rl-filter-bar`, which carries the gap,
            the wrap and the 20px below it, and never shrinks because it is part of the locked
            header region. */}
        <div className="erp-rl-filter-bar">
          <SelectField
            label="Initiative"
            width="lg"
            id="erp-scores-initiative"
            value={initiativeId}
            placeholder={ERP_PROMPT.initiative}
            options={initiatives.map((row) => ({
              value: row.id,
              id: row.name,
              description: row.description,
            }))}
            onChange={(value) =>
              value ? setSearchParams({ initiative: value }) : setSearchParams({})
            }
          />

          <SelectField
            label="Functional area"
            width="lg"
            id="erp-scores-area"
            disabled={!initiativeId || areas.length === 0}
            value={areaFilter}
            /* '' IS A REAL CHOICE — "every area" — so no clear row is added beside it. */
            allowClear={false}
            options={[
              { value: '', label: 'All functional areas' },
              ...areas.map((row) => ({
                value: row.capability_id,
                id: row.code ?? row.name,
                description: row.code ? row.name : undefined,
              })),
            ]}
            onChange={(value) => setParams({ area: value })}
          />

          {/* A four-value enum is not a record, so it stays a native `<select>` — now wearing the
              pattern's `erp-rl-select` (34px, radius 4, 13px) rather than this screen's own
              40px/14px rule. `InitiativeDetailPanel.tsx:404` draws the same record/enum line. The
              LABEL stays: without it the field reads only "All", which says nothing about what is
              being filtered. */}
          <div className="sc-enum">
            <label htmlFor="erp-scores-quadrant">Quadrant:</label>
            <select
              id="erp-scores-quadrant"
              className="erp-rl-select"
              value={quadrantFilter}
              onChange={(e) => setParams({ quadrant: e.target.value })}
            >
              <option value="">All</option>
              {QUADRANT_ORDER.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          {/* The pattern's search box: the `search` ligature INSIDE the field
              (`KnowledgeLibrary.tsx:814-830`), and Clear as a ghost button carrying the `clear`
              ligature (`:871-881`). Clear stays INSIDE this group and still clears the search term
              only — a bar-level Clear would read as "clear every filter", which is not what this
              control has ever done. */}
          <div className="sc-search">
            <label htmlFor="erp-scores-search">Search:</label>
            <div className="erp-rl-search">
              <i className="material-icons">search</i>
              <input
                id="erp-scores-search"
                type="text"
                className="erp-rl-input"
                placeholder="Business unit or system…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {search !== '' && (
              <button
                type="button"
                className="erp-rl-btn erp-rl-btn--ghost"
                onClick={() => setSearch('')}
              >
                <i className="material-icons">clear</i>
                Clear
              </button>
            )}
          </div>
        </div>

        {quadrantCounts !== null && (
          <div className="sc-counts">
            {QUADRANT_ORDER.map((name) => (
              <span
                key={name}
                className="sc-count-badge"
                style={{
                  background: QUADRANT_CHIP[name].pair.bg,
                  color: QUADRANT_CHIP[name].pair.fg,
                }}
                title={QUADRANT_CHIP[name].meaning}
              >
                <span className="sc-count-badge__n">{quadrantCounts[name]}</span> {name}
              </span>
            ))}
            {scoring !== null && (
              <span className="sc-count-badge" style={{ background: '#e8eaed', color: '#3c4043' }}>
                <span className="sc-count-badge__n">{scoring.unitsInInitiative ?? 0}</span> units
                scored
              </span>
            )}
          </div>
        )}

        {initiativeId !== '' && gate === null && (
          <div className="sc-results">
            <span>
              Showing {visible.length} of {scoring?.unitsInInitiative ?? scores.length} assessment
              unit{(scoring?.unitsInInitiative ?? scores.length) === 1 ? '' : 's'}
            </span>
            {(search !== '' || areaFilter !== '' || quadrantFilter !== '') && (
              <span className="sc-filtered-note">
                (filtered{scoring?.unitsInInitiative ? ` from ${scoring.unitsInInitiative}` : ''})
              </span>
            )}
            {scoring !== null && (
              <span className="sc-filtered-note" style={{ fontStyle: 'normal', color: '#5f6368' }}>
                thresholds {tenthsToText(scoring.overlapThresholdPctTenths)}% overlap /{' '}
                {tenthsToText(scoring.complexityThresholdPctTenths)}% complexity ·{' '}
                {scoring.scoringConfigSource === 'stored'
                  ? 'configured definition'
                  : 'default definition'}
              </span>
            )}
          </div>
        )}

        {/* The three states, D-018's: the `sync` ligature spinning for loading, the `error` one
            beside the message, and a large ligature over a title and a sentence when empty. The
            COPY is unchanged — each of the five messages below is the one the screen already
            carried, including the "no data" / "no match" distinction the precedent records at
            `FinalApplicationDispositionModel.tsx:1060-1070`. */}
        {loading ? (
          <div className="erp-rl-loading">
            <i className="material-icons erp-rl-spin">sync</i>
            <p>Loading…</p>
          </div>
        ) : error !== null ? (
          /* `recordList.css` routes the error state through `.erp-panel-error` — the module
             already has one box for "the server refused" — with its `error` ligature beside the
             message. The Retry button this screen has always had stays beneath it. */
          <div className="erp-rl-empty">
            <div className="erp-panel-error">
              <i className="material-icons erp-rl-error-icon">error</i>
              {error}
            </div>
            <button
              type="button"
              className="erp-rl-btn erp-rl-btn--primary"
              onClick={() => void loadScores()}
            >
              Retry
            </button>
          </div>
        ) : initiativeId === '' ? (
          <div className="erp-rl-empty">
            <div className="erp-rl-empty-icon">
              <i className="material-icons">folder_open</i>
            </div>
            <h2 className="erp-rl-empty-title">Select an Initiative</h2>
            <p className="erp-rl-empty-body">
              Every assessment unit of the initiative you choose appears here, scored against its
              baselined capability model.
            </p>
          </div>
        ) : gate !== null ? (
          <div className="sc-gate">
            <div style={{ flex: '1 1 auto', minWidth: 240 }}>
              <div className="sc-gate__title">Nothing is scored yet</div>
              <div className="sc-gate__sub">{gate}</div>
            </div>
            {/* `navigate`, never an `<a href>`: this is one SPA and a real link would reload the
                whole shell. `InitiativesList.tsx:200-206` does the same, and for the same
                reason. */}
            <button
              type="button"
              className="sc-link"
              onClick={() =>
                navigate(
                  `/erp_consolidation/capability-model?initiative=${encodeURIComponent(initiativeId)}`,
                )
              }
            >
              Open the capability model
            </button>
          </div>
        ) : listLoading ? (
          <div className="erp-rl-loading">
            <i className="material-icons erp-rl-spin">sync</i>
            <p>Scoring…</p>
          </div>
        ) : visible.length === 0 ? (
          /* The precedent distinguishes "no data" from "no match" and records why at `:1060-1070`:
             one message for both reads as a broken screen rather than as work not yet done. */
          <div className="erp-rl-empty">
            <div className="erp-rl-empty-icon">
              <i className="material-icons">folder_open</i>
            </div>
            <h2 className="erp-rl-empty-title">
              {scores.length === 0 ? 'No assessment units yet' : 'Nothing matches these filters'}
            </h2>
            <p className="erp-rl-empty-body">
              {scores.length === 0
                ? `${initiative?.name ?? 'This initiative'} has no assessment units to score. They are created when systems are selected for it, and they are scored as soon as the grid has cells.`
                : 'Clear the search, the functional area or the quadrant to see the rest.'}
            </p>
          </div>
        ) : (
          /* `erp-rl-table-wrapper` IS the scroll region — `flex: 1 1 auto; min-height: 0;
             overflow: auto` — and the `th` inside it is sticky, so the heading, the filters and
             the counts above stay put while only the rows move. `sc-cols` adds nothing but this
             table's ten column widths (see its rule). */
          <div className="erp-rl-table-wrapper">
            <table className="erp-rl-table sc-cols">
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('businessUnit')}>
                    Business unit{arrow('businessUnit')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('system')}>
                    System{arrow('system')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('functionalArea')}>
                    Functional area{arrow('functionalArea')}
                  </th>
                  <th className="sortable center" onClick={() => handleSort('overlap')}>
                    Overlap{arrow('overlap')}
                  </th>
                  <th className="sortable center" onClick={() => handleSort('complexity')}>
                    Complexity{arrow('complexity')}
                  </th>
                  <th className="sortable center" onClick={() => handleSort('importance')}>
                    Importance{arrow('importance')}
                  </th>
                  <th className="sortable center" onClick={() => handleSort('quadrant')}>
                    Quadrant{arrow('quadrant')}
                  </th>
                  <th className="sortable center" onClick={() => handleSort('rank')}>
                    Rank{arrow('rank')}
                  </th>
                  <th className="sortable center" onClick={() => handleSort('recorded')}>
                    Recorded{arrow('recorded')}
                  </th>
                  {/* The actions header sits right, as the pattern sets it
                      (`KnowledgeLibrary.tsx:932`; `recordList.css`'s `.erp-rl-right`). */}
                  <th className="erp-rl-right">Breakdown</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => {
                  const quadrant = QUADRANT_CHIP[row.quadrant];
                  return (
                    <tr key={row.assessmentUnitId}>
                      {/* The pattern's primary cell: the record's name at weight 500, with its
                          second line beneath at 12px muted (`erp-rl-name` / `erp-rl-desc`). The
                          system's CODE stays the bold half and its NAME the line under it, as this
                          screen has always shown them. */}
                      <td>
                        <span className="erp-rl-name">
                          {row.businessUnitName ?? row.businessUnitId}
                        </span>
                      </td>
                      <td>
                        {row.application_id_code && (
                          <span className="erp-rl-name">{row.application_id_code}</span>
                        )}
                        <span className="erp-rl-desc">
                          {row.application_name ?? row.applicationId}
                        </span>
                      </td>
                      <td className="erp-rl-sec">
                        {row.functionalAreaName ?? <span className="erp-rl-none">—</span>}
                      </td>
                      <td className="center">
                        {figure(row.overlapDisplay, row.overlapTenThousandths, '#0842a0')}
                      </td>
                      <td className="center">
                        {figure(row.complexityDisplay, row.complexityTenThousandths, '#8c3a00')}
                      </td>
                      <td className="center">
                        {figure(row.importanceDisplay, row.importanceTenThousandths, '#0d652d')}
                      </td>
                      <td className="center">
                        <span
                          className="sc-chip"
                          style={{ background: quadrant.pair.bg, color: quadrant.pair.fg }}
                          title={quadrant.meaning}
                        >
                          {row.quadrant}
                        </span>
                      </td>
                      {/* Numbers do not wrap — `erp-rl-nowrap`, the pattern's 13px secondary
                          column. The centring is this screen's, because the figure above each of
                          these columns is centred and a right-aligned number under a centred bar
                          would not line up with it. */}
                      <td className="center erp-rl-nowrap">{row.priorityRank}</td>
                      <td
                        className="center erp-rl-nowrap"
                        title={`${row.completeness.cellsPresent} of ${row.completeness.capabilities} cells recorded · ${row.completeness.notApplicable} not applicable · ${row.completeness.missing} never recorded`}
                      >
                        {tenthsToText(row.completeness.completeTenths)}%
                      </td>
                      {/* The row action, as D-018's icon pool draws it: an OUTLINED GLYPH button —
                          👁️ in the view ink — with a `title` saying what it does. It opens the
                          same breakdown panel the "Explain" link opened. */}
                      <td>
                        <div className="erp-rl-actions">
                          <button
                            type="button"
                            className="erp-rl-glyph erp-rl-glyph--view"
                            onClick={() => setExplaining(row)}
                            title="Every term of this score, and which cell produced it"
                          >
                            👁️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {explaining !== null && (
          <CalculationBreakdown
            initiativeId={initiativeId}
            assessmentUnitId={explaining.assessmentUnitId}
            onClose={() => setExplaining(null)}
          />
        )}
      </div>
    </ErpScreen>
  );
}
